import { html, LitElement, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import 'Components/vm-button/vm-button';
import type { VmButtonColorScheme, VmButtonVariant } from 'Components/vm-button/vm-button';
import 'Components/vm-icon/vm-icon';
import type { IconName } from 'Components/vm-icon/vm-icon';
import { TriggerController } from 'Components/controllers/trigger-controller';
import { lockScroll, unlockScroll } from 'Utils/dom';
import Styles from './vm-confirm-modal.scss';

/**
 * A focused confirmation dialog: title + Cancel/Confirm buttons. No close button by design -
 * dismissal is via the Cancel button, Escape, or backdrop click.
 *
 * Confirm auto-closes by default (animated). For async work, call `e.preventDefault()`
 * inside the `vm-confirm` handler to keep the modal open and use the `loading` prop while
 * the work is in flight; close manually by setting `open = false` when done.
 *
 * @fires vm-confirm - Fired on confirm-button click. Cancelable: call `preventDefault()` to keep the modal open.
 * @fires vm-cancel  - Fired on cancel-button click, Escape, or backdrop click.
 *
 * @example Sync (auto-close):
 * ```html
 * <vm-confirm-modal
 *   open
 *   heading="Are you sure you want to remove this affiliation?"
 *   cancel-label="Do not remove"
 *   confirm-label="Remove affiliation"
 *   @vm-confirm=${this._removeAffiliation}
 * ></vm-confirm-modal>
 * ```
 *
 * @example Async (keep open with loading):
 * ```ts
 * html`
 *   <vm-confirm-modal
 *     ?open=${this._open}
 *     ?loading=${this._save.isPending}
 *     heading="Delete this photo?"
 *     @vm-confirm=${(e) => { e.preventDefault(); this._save.mutate(); }}
 *   ></vm-confirm-modal>
 * `;
 * ```
 */
@customElement('vm-confirm-modal')
export class VmConfirmModal extends LitElement {
    static override styles = Styles;

    /** Whether the modal is visible. */
    @property({ type: Boolean, reflect: true })
    open = false;

    /**
     * Required. Heading text shown at the top of the modal — both the visible title
     * and the dialog's accessible name (via `aria-labelledby`).
     */
    @property()
    heading!: string;

    /** Label for the confirm button. */
    @property({ attribute: 'confirm-label' })
    confirmLabel = 'Confirm';

    /** Label for the cancel button. */
    @property({ attribute: 'cancel-label' })
    cancelLabel = 'Cancel';

    /**
     * Optional icon rendered above the heading (e.g. `alert-triangle` for an
     * error/warning context). When set, heading + icon are center-aligned as
     * a pair; when unset, the heading renders alone.
     */
    @property({ attribute: 'heading-icon' })
    headingIcon?: IconName;

    /**
     * Confirm button styling. Defaults to `solid` + `error` (destructive intent).
     * Override for non-destructive confirms (e.g. `solid` + `primary`). Pair values
     * must be a valid `<vm-button>` combination — see the vm-button JSDoc.
     */
    @property({ attribute: 'confirm-variant' })
    confirmVariant: VmButtonVariant = 'solid';

    @property({ attribute: 'confirm-color-scheme' })
    confirmColorScheme: VmButtonColorScheme = 'error';

    /**
     * Cancel button styling. Defaults to `outline` + `neutral` — the recessive
     * pair for a destructive-intent dialog.
     */
    @property({ attribute: 'cancel-variant' })
    cancelVariant: VmButtonVariant = 'outline';

    @property({ attribute: 'cancel-color-scheme' })
    cancelColorScheme: VmButtonColorScheme = 'neutral';

    /** Shows a spinner on the confirm button and disables both. Use while awaiting async work. */
    @property({ type: Boolean })
    loading = false;

    /** Disables close-on-backdrop-click. Escape still works. */
    @property({ type: Boolean, reflect: true, attribute: 'disable-backdrop' })
    disableBackdrop = false;

    /**
     * CSS selector for an external element that opens the modal on click.
     * Bound on connect; not observed for changes.
     */
    @property()
    trigger?: string;

    @state()
    private _closing = false;

    @query('dialog')
    private _dialog!: HTMLDialogElement;

    private _triggerCtrl = new TriggerController(this, {
        getSelector: () => this.trigger,
        onActivate: () => {
            this.open = true;
        },
    });

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this.open) unlockScroll();
    }

    override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has('open')) {
            this._triggerCtrl.setExpanded(this.open);
            if (this.open) {
                this._closing = false;
                lockScroll();
                this.updateComplete.then(() => {
                    if (this._dialog && !this._dialog.open) {
                        this._dialog.showModal();
                    }
                });
            } else if (changed.get('open') === true) {
                // Only unlock on a real true→false transition.
                unlockScroll();
                this._triggerCtrl.focus();
            }
        }
    }

    private _handleDialogCancel = (e: Event): void => {
        // Native dialog cancel: fires on Escape and (with closedby="any") on backdrop click.
        e.preventDefault();
        if (this.loading) return;
        this._cancel();
    };

    private _handleBackdropClick = (e: MouseEvent): void => {
        // Fallback for browsers without `closedby` support (e.g. Safari at time of writing).
        if (this.disableBackdrop) return;
        if (this.loading) return;
        if (e.target === this._dialog) this._cancel();
    };

    private _handleCancelClick = (): void => {
        this._cancel();
    };

    private _handleConfirmClick = (): void => {
        if (this.loading) return;
        const ev = new CustomEvent('vm-confirm', { bubbles: true, composed: true, cancelable: true });
        this.dispatchEvent(ev);
        if (!ev.defaultPrevented) {
            this._closing = true;
        }
    };

    private _cancel(): void {
        this.dispatchEvent(new CustomEvent('vm-cancel', { bubbles: true, composed: true }));
        this._closing = true;
    }

    private _handleAnimationEnd = (): void => {
        if (!this._closing) return;
        this._closing = false;
        this._dialog?.close();
        this.open = false;
    };

    override render() {
        if (!this.open) return nothing;

        return html`
            <dialog
                class="vm-confirm-modal ${this._closing ? 'vm-confirm-modal--closing' : ''}"
                role="alertdialog"
                aria-labelledby=${ifDefined(this.heading ? 'vm-confirm-modal-heading' : undefined)}
                closedby=${this.disableBackdrop ? 'closerequest' : 'any'}
                @cancel=${this._handleDialogCancel}
                @click=${this._handleBackdropClick}
                @animationend=${this._handleAnimationEnd}
            >
                <div class="vm-confirm-modal__container">
                    <div
                        class=${classMap({
                            'vm-confirm-modal__heading-wrap': true,
                            'vm-confirm-modal__heading-wrap--with-icon': Boolean(this.headingIcon),
                        })}
                    >
                        ${this.headingIcon
                            ? html`<vm-icon class="vm-confirm-modal__icon" name=${this.headingIcon}></vm-icon>`
                            : nothing}
                        ${this.heading
                            ? html`<h2 id="vm-confirm-modal-heading" class="vm-confirm-modal__heading">
                                  ${this.heading}
                              </h2>`
                            : nothing}
                    </div>
                    <div class="vm-confirm-modal__buttons">
                        <vm-button
                            variant=${this.cancelVariant}
                            color-scheme=${this.cancelColorScheme}
                            ?disabled=${this.loading}
                            @click=${this._handleCancelClick}
                        >
                            ${this.cancelLabel}
                        </vm-button>
                        <vm-button
                            variant=${this.confirmVariant}
                            color-scheme=${this.confirmColorScheme}
                            ?loading=${this.loading}
                            @click=${this._handleConfirmClick}
                        >
                            ${this.confirmLabel}
                        </vm-button>
                    </div>
                </div>
            </dialog>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-confirm-modal': VmConfirmModal;
    }
    interface HTMLElementEventMap {
        'vm-confirm': CustomEvent;
        'vm-cancel': CustomEvent;
    }
}
